import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

import { DeleteMessageScope } from '@/types/chat.type'

interface DeleteMessageDialogProps {
  isOpen: boolean
  isDeleting: boolean
  onClose: () => void
  onConfirm: (scope: DeleteMessageScope) => void
}

export const DeleteMessageDialog: React.FC<DeleteMessageDialogProps> = ({ isOpen, isDeleting, onClose, onConfirm }) => {
  const [selectedScope, setSelectedScope] = React.useState<DeleteMessageScope>(DeleteMessageScope.FOR_ME)

  if (!isOpen) return null

  const handleConfirm = () => {
    onConfirm(selectedScope)
  }

  const handleClose = () => {
    setSelectedScope(DeleteMessageScope.FOR_ME) // Reset to default
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Delete Message</h3>
          </div>
          <div className="space-y-4">
            <p className="text-sm text-gray-800">Choose how you want to delete this message:</p>

            <RadioGroup
              value={selectedScope}
              onValueChange={value => setSelectedScope(value as DeleteMessageScope)}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={DeleteMessageScope.FOR_ME} id="for-me" />
                <Label htmlFor="for-me" className="text-sm font-medium text-gray-900">
                  Delete for me
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={DeleteMessageScope.FOR_EVERYONE} id="for-everyone" />
                <Label htmlFor="for-everyone" className="text-sm font-medium text-gray-900">
                  Delete for everyone
                </Label>
              </div>
            </RadioGroup>

            <div className="text-xs text-gray-600 space-y-1">
              {selectedScope === DeleteMessageScope.FOR_ME && <p>• This message will only be removed from your view</p>}
              {selectedScope === DeleteMessageScope.FOR_EVERYONE && (
                <>
                  <p>• This message will be removed for all participants in the conversation</p>
                  <p className="text-orange-600 font-medium">
                    • Messages can only be deleted for everyone within 5 minutes of being sent
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirm} className="min-w-[80px]" disabled={isDeleting}>
                {isDeleting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Deleting...
                  </div>
                ) : (
                  'Delete'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
